"use client";

import { Telescope, Repeat, Crosshair, Activity, Users, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { advancedFeatures as data } from "@/lib/data/capabilities-narrative-engineering";

const iconMap: Record<string, LucideIcon> = {
  Telescope, Repeat, Crosshair, Activity, Users,
};

const accents = [
  "var(--color-violet)",
  "var(--color-emerald)",
  "var(--color-amber)",
  "var(--color-denim)",
  "var(--color-purple)",
] as const;

export function AdvancedFeatures() {
  return (
    <Section id="advanced-features" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute bottom-1/4 left-1/4 h-80 w-80 rounded-full bg-[var(--color-violet)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-violet)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
          </div>
        </Reveal>

        <div className="mt-12 space-y-5">
          {data.features.map((f, i) => {
            const Icon = iconMap[f.icon] ?? Telescope;
            const accent = accents[i % accents.length]!;
            const reverse = i % 2 === 1;
            return (
              <Reveal key={f.title} delay={i * 80}>
                <div
                  className={`glass rounded-2xl p-7 flex flex-col md:flex-row gap-6 ${reverse ? "md:flex-row-reverse" : ""}`}
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 14%, transparent)`,
                  }}
                >
                  <div className="shrink-0">
                    <span
                      className="grid place-items-center h-14 w-14 rounded-2xl"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-6 w-6" style={{ color: accent }} />
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="eyebrow-tiny" style={{ color: accent }}>
                      Advanced {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-1">{f.title}</h3>
                    <p className="mt-3 text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
